import environments from './environments.json';

/**
 * Configuration Manager for Admin Portal
 * 
 * Priority: Environment Variables > environments.json
 * 
 * Environment Variables (optional, for overrides):
 * - VITE_API_URL: Auth service API URL
 * - VITE_AUTH_PORTAL_URL: Auth portal URL
 */
class Config {
  constructor() {
    this.environments = environments;
    this.currentEnvironment = null;
    this.envOverrides = this.loadEnvOverrides();
    this.loadEnvironment();
  }

  /**
   * Load configuration overrides from environment variables
   */
  loadEnvOverrides() {
    const apiUrl = import.meta.env.VITE_API_URL;
    const authPortalUrl = import.meta.env.VITE_AUTH_PORTAL_URL;

    // Return null if no env vars are set (use environments.json)
    if (!apiUrl && !authPortalUrl) {
      return null;
    }

    return {
      apiUrl,
      authPortalUrl
    };
  }

  /**
   * Load environment from localStorage or default based on hostname
   */
  loadEnvironment() {
    const hostname = window.location.hostname;
    const storedEnv = localStorage.getItem('app_environment');

    // Auto-detect environment based on hostname
    let detectedEnv = 'local';

    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local')) {
      detectedEnv = 'local';
    } else if (hostname.includes('staging.')) {
      detectedEnv = 'staging';
    } else if (hostname.includes('flonube.com')) {
      detectedEnv = 'production';
    } else {
      // Default to production for unknown hostnames (likely deployed)
      detectedEnv = 'production';
    }

    // For production domains, ALWAYS use production config (ignore localStorage)
    // This prevents stale localStorage from causing localhost URLs in production
    if (detectedEnv === 'production' || detectedEnv === 'staging') {
      this.currentEnvironment = detectedEnv;
      localStorage.setItem('app_environment', detectedEnv);
    } else if (storedEnv && this.environments[storedEnv]) {
      // Only use stored environment for local/development
      this.currentEnvironment = storedEnv;
    } else {
      this.currentEnvironment = detectedEnv;
      localStorage.setItem('app_environment', detectedEnv);
    }

    const usingEnvVars = this.envOverrides ? 'YES' : 'NO';
    console.log(`Config loaded: ${this.currentEnvironment} environment (hostname: ${hostname}, using env vars: ${usingEnvVars})`);
  }

  /**
   * Get current environment key
   */
  getEnvironment() {
    return this.currentEnvironment;
  }

  /**
   * Get current environment configuration
   */
  getCurrentConfig() {
    return this.environments[this.currentEnvironment];
  }

  /**
   * Get API URL (env var takes priority)
   */
  get apiUrl() {
    return this.envOverrides?.apiUrl || this.getCurrentConfig().apiUrl;
  }

  /**
   * Get auth portal URL (env var takes priority)
   */
  get authPortalUrl() {
    return this.envOverrides?.authPortalUrl || this.getCurrentConfig().authPortalUrl;
  }

  // Storage Keys (static config)
  get storageKeys() {
    return {
      accessToken: 'platform_access_token',
      refreshToken: 'platform_refresh_token',
      adminSession: 'platform_admin_session'
    };
  }

  // App Info (static config)
  get appName() {
    return 'FLONUBE Admin';
  }

  get appVersion() {
    return '1.0.0';
  }
}

// Create singleton instance
const config = new Config();

export default config;
